import clsx from "clsx";
import LystLogo from "img/branding/lyst-logo-2020-black.svg?inline";
import React from "react";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { JsonLd } from "react-schemaorg";
import { WebSite } from "schema-dts";
import HeaderBarSearch from "web/react/components/app-header/header-bar-search/header-bar-search";
import HeaderGenders from "web/react/components/app-header/header-genders/header-genders";
import UserDropdown, {
    UserDropdownProps,
} from "web/react/components/app-header/pre-header-bar/user-dropdown";
import AutoCompleteProvider from "web/react/components/auto-complete/auto-complete.context";
import { MainNavigationToggleMobile } from "web/react/components/main-navigation/main-navigation-toggle-mobile/main-navigation-toggle-mobile";
import AccountLinks from "web/react/components/user-links/";
import { addLystMatte, removeLystMatte } from "web/redux/ducks/layout";
import { ReduxStoreState } from "web/redux/store";
import { languageIsEnglish } from "web/script/modules/language";
import { AppHeaderBarSerializer } from "web/types/serializers";
import styles from "./header-bar.module.css";

export interface HeaderBarProps {
    homeUrl: AppHeaderBarSerializer["home_url"];
    isHomePage: AppHeaderBarSerializer["is_homepage"];
    genders: AppHeaderBarSerializer["genders"];
    isAuthenticated: AppHeaderBarSerializer["is_authenticated"];
    hasItemsInWishlist: AppHeaderBarSerializer["has_items_in_whishlist"];
    mySearchesUrl: AppHeaderBarSerializer["my_searches_url"];
    savedItemUrl: AppHeaderBarSerializer["saved_item_url"];
    notificationsUrl: AppHeaderBarSerializer["notifications_url"];
    notificationsCount: AppHeaderBarSerializer["notifications_count"];
    searchInput: AppHeaderBarSerializer["search_input"];
    absoluteHomeUrl: AppHeaderBarSerializer["absolute_home_url"];
    absoluteActionUrl: AppHeaderBarSerializer["absolute_action_url"];
    userDropdown: UserDropdownProps;
}

function extractState(state: ReduxStoreState): boolean {
    const { isAssistedSearchOpen, isSearchOpen, isCalloutOpen } = state.appHeaderBarReducer;
    return isAssistedSearchOpen || isSearchOpen || isCalloutOpen;
}

function HeaderBar({
    homeUrl,
    isHomePage,
    genders,
    isAuthenticated,
    mySearchesUrl,
    savedItemUrl,
    notificationsUrl,
    notificationsCount,
    searchInput,
    userDropdown,
    absoluteHomeUrl,
    absoluteActionUrl,
    hasItemsInWishlist,
}: HeaderBarProps): React.ReactElement {
    const showMatte = useReduxSelector(extractState);
    const dispatch = useDispatch();

    if (showMatte) {
        dispatch(addLystMatte());
    } else {
        dispatch(removeLystMatte());
    }
    return (
        <div className={styles.headerBar}>
            <div className={styles.headerBarContainer}>
                <div className={styles.headerBarInner}>
                    <a className={clsx(styles.logo, "hidden-tablet")} href={homeUrl}>
                        <LystLogo />
                    </a>

                    <div className={styles.headerBarLeftContainer}>
                        <div className="hidden-desktop">
                            <MainNavigationToggleMobile />
                        </div>

                        <HeaderGenders genders={genders} />

                        <AutoCompleteProvider>
                            <HeaderBarSearch {...searchInput} />
                            {isHomePage && (
                                <JsonLd<WebSite>
                                    item={{
                                        "@context": "https://schema.org",
                                        "@type": "WebSite",
                                        url: absoluteHomeUrl,
                                        potentialAction: {
                                            "@type": "SearchAction",
                                            target: `${absoluteActionUrl}?q={q}`,
                                            "query-input": "name=q",
                                        },
                                    }}
                                />
                            )}
                        </AutoCompleteProvider>
                    </div>

                    <a className={clsx(styles.logo, "hidden-desktop")} href={homeUrl}>
                        <LystLogo />
                    </a>

                    {languageIsEnglish() ? (
                        <div className={styles.glyphsContainer}>
                            {isAuthenticated && (
                                <AccountLinks
                                    mySearchesUrl={mySearchesUrl}
                                    savedItemUrl={savedItemUrl}
                                    notificationsUrl={notificationsUrl}
                                    notificationsCount={notificationsCount}
                                    hasItemsInWishlist={hasItemsInWishlist}
                                />
                            )}
                            <UserDropdown
                                className="hidden-desktop"
                                accountDropdown={userDropdown.accountDropdown}
                                accountText={userDropdown.accountText}
                                accountUrl={userDropdown.accountUrl}
                                registerUrl={userDropdown.registerUrl}
                            />
                        </div>
                    ) : (
                        <div
                            className={clsx(styles.glyphsContainer, "hidden-desktop")}
                        /> /* maintain flex layout for non-english mobile header bar */
                    )}
                </div>
            </div>
        </div>
    );
}

export default HeaderBar;
